import React, { FC } from 'react';
import Select, { SelectProps, SelectState } from 'react-dropdown-select';
import './checkboxDropdown.scss';
import Checkbox from '../Checkbox';
import type { DropdownOption } from '../Dropdown/types';

interface DropdownProps {
    options: DropdownOption[];
    onSelect: (values: DropdownOption[]) => void;
    values: DropdownOption[];
    endLabel?: string;
    handleEndLabel?: (item: DropdownOption) => void;
    // eslint-disable-next-line react/require-default-props
    onClose?: () => void;
    // eslint-disable-next-line react/require-default-props
    showSelectAll?: boolean;
}

const customStyles = {
    minHeight: '50px',
    borderRadius: '4px',
    border: '0.5px solid #2B2D42',
};

const Dropdown: FC<DropdownProps> = ({ options, onSelect, values = [], endLabel, handleEndLabel, onClose, showSelectAll }) => {

    const removeItem = (item: DropdownOption) => {
        const newValues = values.filter((value) => value.value !== item.value);
        onSelect(newValues);
    };

    const addItem = (item: DropdownOption) => {
        const newValues = [...values, item];
        onSelect(newValues);
    };

    const handleOnChange = () => {
        // console.log('handleOnChange');
    };

    const getContentRenderer = (props: SelectProps<DropdownOption>, state: SelectState<DropdownOption>) => (
        <div className="custom-content-renderer">
            {state.values.length} of {props.options.length} selected
        </div>
    );

    const getItem = (item: DropdownOption) => {
        const valueLabels = values.map((value) => value.label);
        const included = valueLabels.includes(item.label);

        const handleOnClick = () => {
            if (included) {
                removeItem(item);
            } else {
                addItem(item);
            }
        };

        const handleEndLabelClick = () => {
            if (handleEndLabel) {
                handleEndLabel(item);
            }
        };

        return (
            <div className="item-wrapper">
                <Checkbox checked={included} onClick={handleOnClick} />
                <div className="labels-wrapper">
                    <span className="item-label">{item.label}</span>
                    <div onClick={handleEndLabelClick}>
                        <span className="end-label">{endLabel}</span>
                    </div>
                </div>
            </div>
        );
    };

    // "Select all" reflects only the currently available options and preserves
    // any selected values that aren't in `options` (e.g. filtered-out dates).
    const optionValueSet = new Set(options.map((option) => option.value));
    const allSelected = options.length > 0
        && options.every((option) => values.some((value) => value.value === option.value));

    const toggleSelectAll = () => {
        if (allSelected) {
            onSelect(values.filter((value) => !optionValueSet.has(value.value)));
        } else {
            const missing = options.filter((option) => !values.some((value) => value.value === option.value));
            onSelect([...values, ...missing]);
        }
    };

    const dropdownRenderer = () => {
        // No options: don't render the "Select all" row (it would toggle
        // nothing and reads as confusing).
        if (options.length === 0) {
            return <div className="checkbox-dropdown-menu" />;
        }

        return (
            <div className="checkbox-dropdown-menu">
                <div className="item-wrapper select-all-row">
                    <Checkbox checked={allSelected} onClick={toggleSelectAll} />
                    <div className="labels-wrapper">
                        <span className="item-label">Select all</span>
                    </div>
                </div>
                {options.map((item) => (
                    <React.Fragment key={item.value}>{getItem(item)}</React.Fragment>
                ))}
            </div>
        );
    };

    return (
        <div>
            <Select
                contentRenderer={({ props, state }) => getContentRenderer(props, state)}
                searchable={false}
                style={customStyles}
                options={options}
                onChange={handleOnChange}
                values={values}
                multi
                color="#da0812"
                itemRenderer={({ item }) => getItem(item)}
                // Conditional spread required: react-dropdown-select's
                // onDropdownClose rejects undefined with exactOptionalPropertyTypes
                {...(onClose && { onDropdownClose: onClose })}
                {...(showSelectAll && { dropdownRenderer })} />
        </div>
    );
};

Dropdown.defaultProps = {
    endLabel: '',
    handleEndLabel: () => { console.log('end label clicked'); },
    showSelectAll: false,
};

export default Dropdown;
